import {
  Box,
  Checkbox,
  CircularProgress,
  Typography,
  useMediaQuery
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useIsFetching } from "@tanstack/react-query";
import { Upload } from "lucide-react";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { CourseDetailModal } from "./CourseDetailModal";
import { CoursesTable } from "./CoursesTable";
import { ButtonDigited } from "../../components/Button/Button.variants";
import { CommonWidgetContainer } from "../../components/Commons/CommonWidgetContainer.styles";
import { ExternalSidebar } from "../../components/ExternalTraining/ExternalSidebar";
import { useExternalTrainingData } from "../../components/ExternalTraining/ExternalTrainingData";
import { DataResponse, Options } from "../../components/InputCst/MultiSelectCst/types";
import { ExternalTrainingModal } from "../../components/Modal/ExternalTrainingModal";
import { ModalBasic } from "../../components/ModalBasic";
import {
  useCourseTypesOptionsQuery,
  useCampaignsOptionsQuery,
  useKnowledgeDomainsOptionsQuery,
  usePostExternalTrainingConsuntivazione,
  useDeleteExternalTraining,
  useDeleteExternalCertificate
} from "../../queries/skillbook";
import {
  Domain,
  SkillBookExternal
} from "../../routes/Profile/AddSkillBook/AddSkillBook.types";
import { useAuthStore } from "../../stores";
import { downloadBlobFile } from "../../utils/general";
import { onSubmitExternal } from "../../utils/skillBook";
import OutputMessageModal from "../Message/OutputMessageModal.component";



export function CompletedButton({
  completed,
  setCompleted,
  readOnly
}: {
  completed: boolean;
  setCompleted: React.Dispatch<React.SetStateAction<boolean>>;
  readOnly?: boolean;
}) {
  const { t } = useTranslation();
  const theme = useTheme();

  const toggleCompleted = () => {
    if (readOnly) return;
    setCompleted(prev => !prev);
  };

  return (
    <Box
      onClick={ readOnly ? undefined : toggleCompleted }
      aria-pressed={ completed }
      sx={ {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        backgroundColor: theme.customColors.backgroundPrimaryCta,
        borderRadius: "8px",
        padding: "12px 16px",
        cursor: "pointer",
        mt: 2
      } }
    >
      <Typography
        variant="h6"
        fontWeight="bold"
        color={ theme.customColors.textWidgetAlternative }
      >
        { t("comleted_button") }
      </Typography>

      <Checkbox
        checked={ completed }
        disableRipple
        sx={ {
          p: 0,
          "& svg": {
            fontSize: 24
          },
          "& .MuiSvgIcon-root path": {
            fill: theme.customColors.textWidgetAlternative
          }
        } }
        disabled={ readOnly }
      />
    </Box>
  );
}



export function SignupSummaryExternalTraining() {
  const { t } = useTranslation();
  const theme = useTheme();
  const [courseTypes, setCourseTypes] = useState<Options[]>([]);
  const [campaignOptions, setCampaignOptions] = useState<Options[]>([]);
  const [selectedArea, setSelectedArea] = useState<{ isUpdating: boolean; value?: string | number }>({
    isUpdating: false,
    value: ""
  });
  const [filter, setFilter] = useState<string>("all");

  const handleFilterChange = (newFilter: string) => {
    setFilter(newFilter);
    setCurrentPage(1);
    setSelectedStatusFilter([]);
    setSearchTerm("");
    setSelectedCourse(null);
  };

  const [selectedStatusFilter, setSelectedStatusFilter] = useState<string[]>([]); // ARRAY

  const [searchTerm, setSearchTerm] = useState("");

  const [ courses, setCourses] = useState<CourseItem[]>([]);
  const [selectedCourse, setSelectedCourse] = useState<CourseItem | null>(null);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const handleSelectCourse = (course: CourseItem) => {
    setSelectedCourse(course);
    setIsDetailModalOpen(true);
  };
  const handleCloseDetailModal = () => {
    setIsDetailModalOpen(false);
  };
  const [editCourse, setEditCourse] = useState<CourseItem | null>(null);
  const handleEditCourse = (course: CourseItem) => {
    setIsDetailModalOpen(false);
    setEditCourse(course);
    setOpen(true);
  };

  const filteredCourses = courses.filter(course => {
    const matchType =
      filter === "interna"
        ? course.source === "internal"
        : filter === "iscrizione"
          ? course.type?.toLowerCase() === "iscrizione"
          : filter === "consultivazione"
            ? course.type?.toLowerCase() === "consuntivazione"
            : true;

    const matchStatus =
      selectedStatusFilter.length === 0          // array vuoto ⇒ mostra tutto
        ? true
        : selectedStatusFilter.includes(course.status);

    const matchesSearch =
      course.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      course.provider?.toLowerCase().includes(searchTerm.toLowerCase());

    return matchType && matchStatus && matchesSearch;
  });


  //TODO sparisce un'iscrizione che ha lo stesso ID della consuntivazione?
  const mergeCoursesById = (prev: CourseItem[], next: CourseItem[]): CourseItem[] => {
    const combined = [...prev, ...next];
    return Array.from(new Map(combined.map(item => [item.id, item])).values());
  };

  const userId = useAuthStore(state => state.userData?.id_user) ?? "";
  const corporateId = useAuthStore(state => state.userData?.organization_id) ?? "";
  const name = useAuthStore(state => state.userData?.name) ?? "";
  const surname = useAuthStore(state => state.userData?.surname) ?? "";

  useCourseTypesOptionsQuery({
    onError: (error) => {
      console.error("Error fetching data Course Types:", error);
    },
    onSuccess: (data: unknown) => {
      const newCourseTypes = (data as DataResponse[]).map((item: DataResponse) => ({
        label: item.name || item.label,
        value: item.id ? String(item.id) : item.value ? String(item.value) : ""
      }));

      return setCourseTypes(newCourseTypes as Options[]);
    }
  });

  useCampaignsOptionsQuery({
    enabled: Boolean(corporateId),
    onError: (error) => {
      console.error("Error fetching campaigns:", error);
    },
    onSuccess: (data: unknown) => {

      console.log("campaigns fetched:",data);

      const campaigns = (data as { output: DataResponse[] }).output;

      const newCampaignOptions: Options[] = campaigns.map(item => ({
        label: item.name ?? "",
        value: String(item.id)
      }));

      setCampaignOptions(newCampaignOptions);
      console.log("campaigns fetched and cleaned:",campaignOptions);
    }
  });

  useKnowledgeDomainsOptionsQuery({
    enabled: selectedArea.isUpdating && !!selectedArea.value,
    onError: (error) => {
      console.error("Error fetching data Domains:", error);
    },
    onSuccess: (data: { output: DataResponse[] }) => {
      const areaId = selectedArea.isUpdating && selectedArea.value ? selectedArea.value.toString() : "";
      if (!areaId) return;

      setSelectedArea({ isUpdating: false });
      console.log("Data domain fetched successfully:", data);
    },
    knowledgeAreaId: selectedArea.isUpdating && selectedArea.value ? selectedArea.value.toString() : ""
  });

  // const [coursesAreLoading, setCoursesAreLoading] = useState<boolean>(true);

  const anyFetching = useIsFetching({
    predicate: q =>
      Array.isArray(q.queryKey) &&
      (q.queryKey[0] === "externalCert" || q.queryKey[0] === "externalTrain")
  }) > 0;
  const coursesAreLoading = courses.length === 0 && anyFetching;

  const handleUploadCertificate = (course: CourseItem, file: File) => {
    const defaultOptionTrainingMode = courseTypes.find(
      (opt) => opt.label === course.trainingMode
    ) || { label: "-", value: "" };

    const data: Partial<SkillBookExternal> = {
      area_domain: course.area
        ? course.area.flatMap((areaItem) =>
          (areaItem.domain || []).map((domainItem: Domain) => ({
            knowledge_area: { label: areaItem.label, value: areaItem.id },
            knowledge_domain: { label: domainItem.label, value: domainItem.id }
          }))
        )
        : [],
      attachments: [],
      campaign: course.campaign ?? "",
      campaignId: course.campaignId ?? "",
      capability: course.capability?.map((c) => ({ label: c.label, value: c.id })) ?? [],
      cost: course.cost,
      customFields: course.customFields ?? [],
      data_fine_validita: course.certificateEndValidityDate ?? "",
      data_inizio_validita: course.certificateStartValidityDate ?? "",
      description: course.description,
      duration: course.duration ?? 0,
      durationParticipation: String(course.durationParticipation ?? 0),
      endDate: course.endDate ?? "",
      file: file as unknown as string,
      link_certificato: course.certificate_url ?? "",
      motivation: course.motivation ?? "",
      plan: course.plan ?? "",
      provider: course.provider,
      source: course.source,
      startDate: course.startDate,
      teacher: course.instructor ?? "",
      title: course.title,
      trainingType: defaultOptionTrainingMode as unknown as SkillBookExternal["trainingType"]
    };

    onSubmitExternal(
      data as unknown as SkillBookExternal,
      mutate as unknown as (data: FormData) => void,
      course.id
    );
  };

  const handleDownloadCertificate = async (url: string) => {
    try {
      const response = await fetch(url, { method: "GET" });
      if (!response.ok) throw new Error(`HTTP error ${response.status}`);
      const blob = await response.blob();
      downloadBlobFile({ fileName: "certificato.pdf", blobFile: blob });
    } catch (err) {
      console.error("Errore durante il download del PDF:", err);
      window.open(url, "_blank");
    }
  };


  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    setCourses([]);
  }, [refreshKey]);

  const handleRefresh = () => {
    setCourses([]);
    setRefreshKey(prev => prev + 1);
  };

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [deleteSuccess, setDeleteSuccess] = useState<boolean | null>(null);
  const [courseToDelete, setCourseToDelete] = useState<CourseItem | null>(null);
  const [isConfirmDeleteOpen, setIsConfirmDeleteOpen] = useState(false);

  const handleRequestDelete = (course: CourseItem) => {
    setCourseToDelete(course);
    setIsDetailModalOpen(false);
    setIsConfirmDeleteOpen(true);
  };

  const handleCancelDelete = () => {
    setIsConfirmDeleteOpen(false);
    setCourseToDelete(null);
  };

  const onDeleteSuccess = () => {
    setDeleteSuccess(true);
    setIsConfirmDeleteOpen(false);
    setIsDeleteModalOpen(true);
  };

  const onDeleteError = () => {
    setDeleteSuccess(false);
    setIsConfirmDeleteOpen(false);
    setIsDeleteModalOpen(true);
  };

  const { mutate: deleteExternalTraining, isPending: isDeletingTraining } = useDeleteExternalTraining({
    onError: onDeleteError,
    onSuccess: onDeleteSuccess
  });
  const { mutate: deleteExternalCertificate, isPending: isDeletingCertificate } = useDeleteExternalCertificate({
    onError: onDeleteError,
    onSuccess: onDeleteSuccess
  });
  const isDeleting = isDeletingTraining || isDeletingCertificate;

  const handleConfirmDelete = () => {
    if (!courseToDelete) return;
    const id = Number(courseToDelete.id);
    if (courseToDelete.type === "Consuntivazione") {
      deleteExternalCertificate(id);
    } else {
      deleteExternalTraining(id);
    }
  };

  const [currentPage, setCurrentPage] = useState(1);
  const [sortKey, setSortKey] = useState<"status" | "type" | "startDate" | null>(null);
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");

  const handleSortChange = (key: "status" | "type" | "startDate" | null) => {
    if (sortKey === key) {
      setSortDirection(prev => prev === "asc" ? "desc" : "asc");
    } else {
      setSortKey(key);
      setSortDirection("asc");
    }
    setCurrentPage(1);
  };

  const itemsPerPage = 10;
  const sortedCourses = [...filteredCourses].sort((a, b) => {
    if (!sortKey) {
      const dateA = a.endDate ? new Date(a.endDate).getTime() : 0;
      const dateB = b.endDate ? new Date(b.endDate).getTime() : 0;
      return dateB - dateA;
    }
    const dir = sortDirection === "asc" ? 1 : -1;
    if (sortKey === "status") {
      return dir * (a.status ?? "").localeCompare(b.status ?? "");
    }
    if (sortKey === "type") {
      const valA = a.provider || a.type || "";
      const valB = b.provider || b.type || "";
      return dir * valA.localeCompare(valB);
    }
    if (sortKey === "startDate") {
      const dateA = a.startDate ? new Date(a.startDate).getTime() : 0;
      const dateB = b.startDate ? new Date(b.startDate).getTime() : 0;
      return dir * (dateA - dateB);
    }
    return 0;
  });

  const paginatedCourses = sortedCourses.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  useExternalTrainingData(userId,corporateId,name,surname,setCourses,mergeCoursesById,refreshKey);

  const { mutate } = usePostExternalTrainingConsuntivazione({
    onSuccess: (data: unknown) => {
      console.log("Successo:", data);
      // setOpen(true);
      setSubmitSucceeded(true);
      setIsOutputModalOpen(true);
      handleRefresh();
    },
    onError: (error) => {
      console.error("Errore:", error);
      // setOpen(true);
      setSubmitSucceeded(false);
      setIsOutputModalOpen(true);
    }
  });

  const handleCloseConfirmModal = () => {
    console.log("handle close confirm modal");
    setOpen(false);
    handleClose();
    setRefreshKey(prev => prev + 1);
  };

  const handleCloseModalAndRefresh = () => {
    console.log("handle close modal and refresh print, refreshKey: ",refreshKey);
    setOpen(false);
    setRefreshKey(prev => prev + 1);
    //window.location.reload();
  };

  const [open, setOpen] = useState(false);

  const handleOpen = () => {
    setEditCourse(null);
    setOpen(true);
  };

  const handleClose = () => setOpen(false);

  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const _isLarge = useMediaQuery(theme.breakpoints.down("lg"));
  const [isOutputModalOpen, setIsOutputModalOpen] = React.useState(false);
  const handleCloseWithRefresh = () => {
    setIsOutputModalOpen(false);
    setRefreshKey(prev => prev + 1);
  };
  const [submitSucceeded, setSubmitSucceeded] = useState<boolean>();


  if (coursesAreLoading) {
    return (
      <Box
        sx={ { display: "flex", justifyContent: "center", alignItems: "center", minHeight: 400 } }
      >
        <CircularProgress color="secondary" />
      </Box>
    );
  }




  return (
    <>
      { /* === HEADER: CV Formativo === */ }
      <Box
        sx={ {
          alignItems: isMobile ? "flex-start" : "center",
          display: "flex",
          flexDirection: isMobile ? "column" : "row",
          gap: 2,
          justifyContent: "space-between",
          mb: 3,
          width: "100%"
        } }
      >
        <Box>
          <Typography
            color={ theme.customColors.textPrimary }
            fontWeight={ 700 }
            sx={ { mb: 0.5 } }
            variant="h5"
            fontSize={ "1.25rem" }
          >
            { t("your_external_training") }
          </Typography>
          <Typography
            color={ theme.customColors.textPrimary }
            fontSize="1rem"
            sx={ { opacity: 0.7 } }
          >
            { t("external_training_description") }
          </Typography>
        </Box>
        <ButtonDigited
          typology="primary"
          onClick={ handleOpen }
        >
          <>
            <Typography
              fontSize={ "0.875rem" }
              fontWeight={ "600" }
            >
              { t("upload_external_training") }
            </Typography>
            <Upload size={ "1rem" } fontWeight={ "600" } />
          </>
        </ButtonDigited>

        { /* TODO:verrà visualizzato in seguito
          <Button
            onClick={ () => {
              // TODO: collegare download CSV
            } }
            sx={ {
              borderColor: theme.customColors.border,
              borderRadius: "8px",
              color: theme.customColors.textPrimary,
              fontSize: "0.85rem",
              px: 2,
              py: 1,
              textTransform: "none"
            } }
            variant="outlined"
          >
            { t("download_formative_cv") }
            <Icon
              icon="Icons_download"
              size={ 18 }
              sx={ { marginLeft: 1 } }
            />
          </Button>
          */ }
      </Box>

      <ModalBasic
        open={ open }
        title={ "" }
        content={
          <ExternalTrainingModal
            closeModal={ handleCloseModalAndRefresh }
            initialData={ editCourse ?? undefined }
          />
        }
        handleClose={ handleCloseConfirmModal }
        size={ { height: "auto", maxHeight: "90vh" } }
      />

      { /* === FILTRI + TABELLA === */ }
      { courses.length > 0 && (
        <CommonWidgetContainer
          sx={ {
            alignItems: "flex-start",
            borderRadius: "16px",
            color: theme.customColors.exploreCardText,
            display: "flex",
            flexDirection: "column",
            gap: 0,
            maxWidth: "100%",
            mt: 2,
            width: "100%",
            padding: "0"
          } }
        >
          { /* Filtri inline */ }
          <Box
            sx={ {
              p: 2,
              width: "100%",
              height: "2.2rem"
            } }
          >
            <ExternalSidebar
              filter={ filter }
              onFilterChange={ handleFilterChange }
              selectedStatus={ selectedStatusFilter }
              setSelectedStatus={ setSelectedStatusFilter }
              searchTerm={ searchTerm }
              setSearchTerm={ setSearchTerm }
            />
          </Box>

          { /* Tabella dei risultati */ }
          <Box sx={ { p: 2, width: "100%" } }>
            <CoursesTable
              courses={ filteredCourses }
              selectedCourse={ selectedCourse }
              onSelect={ handleSelectCourse }
              filter={ filter }
              paginatedCourses={ paginatedCourses }
              currentPage={ currentPage }
              itemsPerPage={ itemsPerPage }
              setCurrentPage={ setCurrentPage }
              sortKey={ sortKey }
              sortDirection={ sortDirection }
              onSortChange={ handleSortChange }
              onUploadCertificate={ handleUploadCertificate }
              onMarkAsCompleted={ (course) => {
                setSelectedCourse(course);
              } }
            />
          </Box>
        </CommonWidgetContainer>
      ) }

      { /* Modals */ }
      <ModalBasic
        open={ isOutputModalOpen }
        title={ "" }
        content={
          <OutputMessageModal
            success={ submitSucceeded ?? false }
            closeModal={ handleCloseWithRefresh }
          />
        }
        handleClose={ handleCloseWithRefresh }
        size={ { height: "auto", maxHeight: "90vh", width: { sm: "40vw", xs: "100vw" } } }
      />

      <ModalBasic
        open={ isDeleteModalOpen }
        title={ "" }
        content={
          <Box textAlign="center">
            <Typography variant="h5" fontWeight={ 700 } sx={ { mb: 2 } }>
              { deleteSuccess
                ? t("delete_success")
                : t("delete_failed") }
            </Typography>
            <Typography variant="body1" sx={ { mb: 3 } }>
              { deleteSuccess
                ? t("delete_course_success")
                : t("delete_course_failed") }
            </Typography>
            <Box sx={ { display: "flex", justifyContent: "center" } }>
              <ButtonDigited
                typology="primary"
                onClick={ () => {
                  setIsDeleteModalOpen(false);
                  setDeleteSuccess(null);
                  handleRefresh();
                } }
                text={ t("close") }
              />
            </Box>
          </Box>
        }
        handleClose={ handleRefresh }
        size={ { height: "auto", maxHeight: "90vh", width: { sm: "30vw", xs: "100vw" } } }
      />

      { /* Modale dettaglio corso */ }
      { selectedCourse && (
        <CourseDetailModal
          open={ isDetailModalOpen }
          course={ selectedCourse }
          onClose={ handleCloseDetailModal }
          onDownloadCertificate={ handleDownloadCertificate }
          onUploadCertificate={ handleUploadCertificate }
          onEdit={ handleEditCourse }
          onDelete={ handleRequestDelete }
        />
      ) }

      { /* Modale di conferma eliminazione */ }
      <ModalBasic
        open={ isConfirmDeleteOpen }
        title={ "" }
        content={
          <Box textAlign="center">
            <Typography variant="h5" fontWeight={ 700 } sx={ { mb: 2 } }>
              { t("delete_confirm_title") }
            </Typography>
            <Typography variant="body1" sx={ { mb: 3 } }>
              { t("delete_confirm_message", { title: courseToDelete?.title ?? "" }) }
            </Typography>
            <Box sx={ { display: "flex", gap: 2, justifyContent: "center" } }>
              <ButtonDigited
                typology="secondary"
                onClick={ handleCancelDelete }
                text={ t("annul") }
                disabled={ isDeleting }
              />
              <ButtonDigited
                typology="primary"
                onClick={ handleConfirmDelete }
                disabled={ isDeleting }
              >
                <>
                  { isDeleting && <CircularProgress size={ 16 } color="inherit" /> }
                  <span>{ t("delete") }</span>
                </>
              </ButtonDigited>
            </Box>
          </Box>
        }
        handleClose={ handleCancelDelete }
        size={ { height: "auto", maxHeight: "90vh", width: { sm: "30vw", xs: "100vw" } } }
      />
    </>
  );
}
